import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Heart, Layers, MessageCircle, Play } from 'lucide-react'
import { SocialPostItem } from '@digital-twins/shared-types'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { formatSocialCount, formatSocialDate } from './socialFormat'

interface SocialPostTileProps {
  post: SocialPostItem
  /** Names of the tracker topics the post was tagged with. */
  topicNames?: Array<string>
  onOpen: (postToken: string) => void
}

export function SocialPostTile(props: SocialPostTileProps) {
  const { post, topicNames = [], onOpen } = props
  const { t, i18n } = useTranslation('app')
  const [hasImageFailed, setHasImageFailed] = useState(false)

  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')
  const date = formatSocialDate(post.publishedAt ?? post.collectedAt, i18n.language)
  const isShowingImage = post.media.thumbnailUrl != null && !hasImageFailed
  const { likes, comments } = post.engagement

  return (
    <button
      type="button"
      onClick={() => onOpen(post.token)}
      aria-label={t('socialListening.detail.openPost', { author })}
      className="group relative flex aspect-[4/5] w-full flex-col overflow-hidden rounded-xl border border-border bg-muted text-left transition hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`social-post-${post.token}`}
    >
      {isShowingImage ? (
        <>
          <img
            src={post.media.thumbnailUrl as string}
            alt=""
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setHasImageFailed(true)}
            className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
          />
          <span className="flex-1" />
        </>
      ) : (
        <span className="min-h-0 flex-1 overflow-hidden whitespace-pre-line p-4 pt-11 text-[13px] leading-snug text-foreground [mask-image:linear-gradient(to_bottom,black_75%,transparent)]">
          {post.text}
        </span>
      )}

      <span className="absolute left-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
        <SocialPlatformIcon platform={post.platform} className="h-3.5 w-3.5" />
      </span>
      {post.media.hasVideo || post.media.imageCount > 1 ? (
        <span className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
          {post.media.hasVideo ? (
            <Play className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
          ) : (
            <Layers className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </span>
      ) : null}

      <span
        className={`relative space-y-1 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-3 text-white ${isShowingImage ? 'pt-10' : 'pt-6'}`}
      >
        {topicNames.length > 0 ? (
          <span className="flex flex-wrap gap-1" data-testid="social-post-topics">
            {topicNames.map((name) => (
              <span
                key={name}
                className="max-w-full truncate rounded-full bg-white/20 px-2 py-0.5 text-[11px] backdrop-blur-sm"
              >
                {name}
              </span>
            ))}
          </span>
        ) : null}
        <span className="block truncate text-[13px] font-medium">{author}</span>
        <span className="flex items-center gap-3 text-[12px] text-white/80">
          {date ? <span>{date}</span> : null}
          {likes != null ? (
            <span className="inline-flex items-center gap-1">
              <Heart className="h-3 w-3" aria-hidden="true" />
              {formatSocialCount(likes, i18n.language)}
            </span>
          ) : null}
          {comments != null ? (
            <span className="inline-flex items-center gap-1">
              <MessageCircle className="h-3 w-3" aria-hidden="true" />
              {formatSocialCount(comments, i18n.language)}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  )
}
